import {
  ACC_MOVIL, BRD_MOVIL, LABEL_MOVIL,
  estiloAddBtnMovil,
} from './agenteCamposVinculados';
import InpCampoMovil, { campoTieneValor } from './InpCampoMovil';
import AddressAutocompleteInput from '../AddressAutocompleteInput';
import { PROVINCIAS_ES } from '../../utils/provinciasEs';
import { parseTipoViaCalle } from '../../utils/parseTipoViaCalle';
import { resolverProvinciaId, resolverPoblacionPlaces } from '../../utils/resolverLocalidadDesdePlaces';
import { normalizeAuxSelectValue } from '../../config/auxCamposPipeline';
import {
  buildObligatoriosPersonaDesdeConfig,
  debeMostrarCampoPersona,
  docPareceCif,
  esCampoPropietarioConfig,
  faltantesPersonaConPrincipal,
  faltaTelefonoPrincipalEnState,
  validarTelefonoPrincipalState,
  telefonoPersonaValido,
  PERSONA_BASICA_KEYS,
  PERSONA_DOMICILIO_KEYS,
  sanearPartesDomicilioPersona,
} from '../../utils/propietarioCamposConfig';
import './agenteMovilCampos.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useContactoSoftWarnings } from '../../hooks/useContactoSoftWarnings';
import { debeRevisarDocumentoEnInput } from '../../utils/contactoSoftValidation';

export { esCampoPropietarioConfig };

const labelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: LABEL_MOVIL,
  WebkitTextFillColor: LABEL_MOVIL,
  textTransform: 'uppercase',
  letterSpacing: '0.07em',
  margin: '10px 0 6px',
};

const emptyPersona = () => ({
  nombre: '',
  apellidos: '',
  tipo_via_id: '',
  tipo_via: '',
  domicilio_base: '',
  numero: '',
  bloque: '',
  escalera: '',
  piso: '',
  puerta: '',
  cp: '',
  poblacion: '',
  provincia: '',
  pais: 'ES',
  dni: '',
  telefono: '',
  email: '',
  porcentaje: '',
});

const PAISES_DOMICILIO = [
  { code: 'ES', nombre: 'España' },
  { code: 'FR', nombre: 'Francia' },
  { code: 'PT', nombre: 'Portugal' },
  { code: 'DE', nombre: 'Alemania' },
  { code: 'IT', nombre: 'Italia' },
  { code: 'GB', nombre: 'Reino Unido' },
  { code: 'BE', nombre: 'Bélgica' },
  { code: 'NL', nombre: 'Países Bajos' },
  { code: 'CH', nombre: 'Suiza' },
  { code: 'AD', nombre: 'Andorra' },
  { code: 'OTHER', nombre: 'Otro' },
];

const rowPartesStyle = { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 };
const cellParteStyle = { flex: '1 1 58px', minWidth: 58 };

function componerDomicilio(p) {
  const base = String(p.domicilio_base || '').trim();
  const resto = [p.cp, p.poblacion, p.provincia].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
  return [base, resto].filter(Boolean).join(', ');
}

/** Calle (+ nº/bloque/…) sin CP/población/provincia — van en campos propios. */
function callePersonaTexto(p) {
  const bits = [String(p?.domicilio_base || '').trim()];
  const num = String(p?.numero || '').trim();
  if (num) bits.push(num);
  const bloq = String(p?.bloque || '').trim();
  if (bloq) bits.push(`Blq ${bloq}`);
  const esc = String(p?.escalera || '').trim();
  if (esc) bits.push(`Esc ${esc}`);
  const piso = String(p?.piso || '').trim();
  if (piso) bits.push(piso);
  const pta = String(p?.puerta || '').trim();
  if (pta) bits.push(pta);
  return bits.filter(Boolean).join(', ');
}

/**
 * Mapea una entrada de `propietarios_ficha` / `contactos_asociados_ficha`
 * (tab "Propietarios y contactos" del CRM escritorio) a la forma del bloque
 * del agente. Fuente adicional: no sustituye a las claves móviles, solo
 * rellena huecos (ver .cursor/rules/scraping-ampliar-no-sustituir + mismo
 * espíritu para captación).
 */
function personaDesdeFicha(p) {
  if (!p || typeof p !== 'object') return null;
  const base = String(p.domicilio_base || '').trim();
  const dir = String(p.direccion || '').trim();
  // Priorizar calle limpia; no meter un `direccion` compuesto (CP/pob/prov) en domicilio_base
  const domicilioBase = base
    || (dir && !/\b\d{5}\b/.test(dir) ? dir : '')
    || '';
  return sanearPartesDomicilioPersona({
    nombre: p.nombre || '',
    apellidos: p.apellidos || '',
    tipo_via_id: p.tipo_via_id || '',
    tipo_via: p.tipo_via || '',
    domicilio_base: domicilioBase,
    numero: p.numero || p.domicilio_numero || '',
    bloque: p.bloque || p.domicilio_bloque || '',
    escalera: p.escalera || p.domicilio_escalera || '',
    piso: p.piso || p.domicilio_piso || '',
    puerta: p.puerta || p.domicilio_puerta || '',
    cp: p.codigo_postal || p.cp || '',
    poblacion: p.poblacion || '',
    provincia: p.provincia || '',
    pais: p.pais || p.domicilio_pais || 'ES',
    dni: p.dni_nie || p.dni || '',
    telefono: p.telefono || '',
    email: p.email || '',
    porcentaje: p.porcentaje != null ? String(p.porcentaje) : '',
  });
}

/** ¿Hay domicilio del propietario guardado (no confundir con dirección del inmueble)? */
function tieneDomicilioPropietarioGuardado(ex, fichaTitular) {
  if (String(ex.domicilio_cp || '').trim()) return true;
  if (String(ex.domicilio_poblacion || '').trim()) return true;
  if (String(ex.domicilio_base || '').trim()) return true;
  if (String(ex.domicilio || '').trim() && (ex.propietario_nombre || ex.dni || fichaTitular)) return true;
  if (fichaTitular && (String(fichaTitular.domicilio_base || '').trim() || String(fichaTitular.cp || '').trim())) return true;
  return false;
}

/** Hidrata el estado del bloque desde datos_extras / captación. */
export function hidratarPropietariosDesdeExtras(extras = {}, captacion = {}) {
  const ex = extras && typeof extras === 'object' ? extras : {};
  // Datos ya guardados desde el tab "Propietarios y contactos" del CRM
  // escritorio (PropiedadForm → construirDatosCaptacion). Si la secretaria
  // los rellenó ahí, usarlos como fuente adicional cuando falte el dato móvil.
  const fichaPropietarios = Array.isArray(ex.propietarios_ficha) ? ex.propietarios_ficha : [];
  const fichaContactos = Array.isArray(ex.contactos_asociados_ficha) ? ex.contactos_asociados_ficha : [];
  const fichaTitular = personaDesdeFicha(fichaPropietarios[0]);
  const domPropGuardado = tieneDomicilioPropietarioGuardado(ex, fichaTitular);

  let titular = {
    nombre: String(ex.propietario_nombre || captacion.nombre || fichaTitular?.nombre || '').trim(),
    apellidos: String(
      ex.propietario_apellidos
      || captacion.apellido
      || captacion.apellidos
      || fichaTitular?.apellidos
      || ''
    ).trim(),
    tipo_via_id: String(ex.domicilio_tipo_via_id || ex.propietario_tipo_via_id || fichaTitular?.tipo_via_id || '').trim(),
    tipo_via: String(ex.domicilio_tipo_via || ex.propietario_tipo_via || fichaTitular?.tipo_via || '').trim(),
    // Domicilio propietario: solo claves domicilio_* / ficha — no calle/CP/población del inmueble.
    domicilio_base: String(ex.domicilio_base || fichaTitular?.domicilio_base || '').trim(),
    numero: String(ex.domicilio_numero || fichaTitular?.numero || '').trim(),
    bloque: String(ex.domicilio_bloque || fichaTitular?.bloque || '').trim(),
    escalera: String(ex.domicilio_escalera || fichaTitular?.escalera || '').trim(),
    piso: String(ex.domicilio_piso || fichaTitular?.piso || '').trim(),
    puerta: String(ex.domicilio_puerta || fichaTitular?.puerta || '').trim(),
    cp: String(ex.domicilio_cp || fichaTitular?.cp || '').trim(),
    poblacion: String(ex.domicilio_poblacion || fichaTitular?.poblacion || '').trim(),
    provincia: String(ex.domicilio_provincia || fichaTitular?.provincia || '').trim(),
    pais: String(ex.domicilio_pais || fichaTitular?.pais || 'ES').trim() || 'ES',
    dni: String(ex.dni || fichaTitular?.dni || '').trim(),
    telefono: String(ex.telefono || ex.propietario_telefono || captacion.telefono || fichaTitular?.telefono || '').trim(),
    email: String(ex.email || captacion.email || fichaTitular?.email || '').trim(),
    porcentaje: ex.porcentaje_propiedad != null && ex.porcentaje_propiedad !== ''
      ? String(ex.porcentaje_propiedad)
      : (fichaTitular?.porcentaje || '100'),
  };
  if (!titular.nombre && ex.propietario) {
    const parts = String(ex.propietario).trim().split(/\s+/);
    titular.nombre = parts[0] || '';
    titular.apellidos = parts.slice(1).join(' ');
  }
  if (!titular.domicilio_base && domPropGuardado && ex.domicilio) {
    titular.domicilio_base = String(ex.domicilio).trim();
  }
  titular = sanearPartesDomicilioPersona(titular);
  const propietariosExtra = Array.isArray(ex.propietarios_extra) && ex.propietarios_extra.length > 0
    ? ex.propietarios_extra.map((p) => sanearPartesDomicilioPersona({
      ...emptyPersona(),
      nombre: p?.nombre || '',
      apellidos: p?.apellidos || '',
      domicilio_base: p?.domicilio_base || '',
      numero: p?.numero || '',
      bloque: p?.bloque || '',
      escalera: p?.escalera || '',
      piso: p?.piso || '',
      puerta: p?.puerta || '',
      cp: p?.cp || p?.codigo_postal || '',
      poblacion: p?.poblacion || '',
      provincia: p?.provincia || '',
      pais: p?.pais || 'ES',
      dni: p?.dni || '',
      telefono: p?.telefono || '',
      email: p?.email || '',
      porcentaje: p?.porcentaje != null ? String(p.porcentaje) : '',
    }))
    // Sin datos móviles: usar el resto de propietarios ya dados de alta en el tab CRM
    : fichaPropietarios.slice(1).map((p) => ({ ...emptyPersona(), ...personaDesdeFicha(p) }));
  const contactosExtra = Array.isArray(ex.contactos_extra) && ex.contactos_extra.length > 0
    ? ex.contactos_extra.map((p) => sanearPartesDomicilioPersona({
      ...emptyPersona(),
      nombre: p?.nombre || '',
      apellidos: p?.apellidos || '',
      domicilio_base: p?.domicilio_base || '',
      numero: p?.numero || '',
      bloque: p?.bloque || '',
      escalera: p?.escalera || '',
      piso: p?.piso || '',
      puerta: p?.puerta || '',
      cp: p?.cp || p?.codigo_postal || '',
      poblacion: p?.poblacion || '',
      provincia: p?.provincia || '',
      pais: p?.pais || 'ES',
      dni: p?.dni || '',
      telefono: p?.telefono || '',
      email: p?.email || '',
    }))
    // Sin datos móviles: usar los contactos asociados ya dados de alta en el tab CRM
    : fichaContactos.map((p) => ({ ...emptyPersona(), ...personaDesdeFicha(p) }));
  return {
    titular,
    propietariosExtra,
    contactosExtra,
    comunicacionKey: ex.comunicacion_key || 'titular',
  };
}

function personaAFicha(p, { id, comunicacionKey, comKey }) {
  const nom = String(p?.nombre || '').trim();
  const ape = String(p?.apellidos || '').trim();
  if (!nom && !ape) return null;
  return {
    id,
    nombre: nom,
    apellidos: ape,
    telefono: p.telefono || '',
    email: p.email || '',
    dni_nie: p.dni || '',
    tipo_via_id: p.tipo_via_id || '',
    tipo_via: p.tipo_via || '',
    domicilio_base: p.domicilio_base || '',
    numero: p.numero || '',
    bloque: p.bloque || '',
    escalera: p.escalera || '',
    piso: p.piso || '',
    puerta: p.puerta || '',
    codigo_postal: p.cp || '',
    poblacion: p.poblacion || '',
    provincia: p.provincia || '',
    pais: p.pais || 'ES',
    // Solo calle (+ partes); CP/población/provincia van en sus campos
    direccion: callePersonaTexto(p),
    porcentaje: p.porcentaje !== '' && p.porcentaje != null ? Number(p.porcentaje) : undefined,
    comunicacion: comunicacionKey === comKey,
    contacto_principal: comunicacionKey === comKey,
  };
}

/** Misma estructura que cap4BuildPropietariosFicha (móvil) para datos_extras y ficha CRM. */
export function buildPropietariosFichaFromState(state) {
  if (!state?.titular) return [];
  const comKey = state.comunicacionKey || 'titular';
  const list = [];
  const titular = personaAFicha(state.titular, { id: 'titular', comunicacionKey: comKey, comKey: 'titular' });
  if (titular) {
    if (titular.porcentaje == null) titular.porcentaje = 100;
    list.push(titular);
  }
  (state.propietariosExtra || []).forEach((p, i) => {
    const row = personaAFicha(p, { id: `prop:${i}`, comunicacionKey: comKey, comKey: `prop:${i}` });
    if (row) list.push(row);
  });
  return list;
}

export function buildContactosAsociadosFichaFromState(state) {
  if (!state) return [];
  const comKey = state.comunicacionKey || 'titular';
  return (state.contactosExtra || [])
    .map((p, i) => personaAFicha(p, { id: `cont:${i}`, comunicacionKey: comKey, comKey: `cont:${i}` }))
    .filter(Boolean);
}

/** propietarios_ficha desde extras (array guardado o claves planas del modal agente). */
export function propietariosFichaDesdeExtras(extras = {}) {
  const ex = extras && typeof extras === 'object' ? extras : {};
  if (Array.isArray(ex.propietarios_ficha) && ex.propietarios_ficha.length > 0) {
    return ex.propietarios_ficha;
  }
  return buildPropietariosFichaFromState(hidratarPropietariosDesdeExtras(ex, {}));
}

export function contactosAsociadosFichaDesdeExtras(extras = {}) {
  const ex = extras && typeof extras === 'object' ? extras : {};
  if (Array.isArray(ex.contactos_asociados_ficha) && ex.contactos_asociados_ficha.length > 0) {
    return ex.contactos_asociados_ficha;
  }
  return buildContactosAsociadosFichaFromState(hidratarPropietariosDesdeExtras(ex, {}));
}

/** Patch para datos_extras al guardar. */
export function patchPropietariosParaExtras(state) {
  if (!state?.titular) return {};
  const t = state.titular;
  const nombreCompleto = [t.nombre, t.apellidos].map((x) => String(x || '').trim()).filter(Boolean).join(' ');
  const domicilio = componerDomicilio(t);
  return {
    propietario_nombre: t.nombre || '',
    propietario_apellidos: t.apellidos || '',
    propietario: nombreCompleto || undefined,
    propietario_telefono: t.telefono || '',
    telefono: t.telefono || '',
    email: t.email || '',
    dni: t.dni || '',
    domicilio_tipo_via_id: t.tipo_via_id || '',
    domicilio_tipo_via: t.tipo_via || '',
    domicilio_base: t.domicilio_base || '',
    domicilio_numero: t.numero || '',
    domicilio_bloque: t.bloque || '',
    domicilio_escalera: t.escalera || '',
    domicilio_piso: t.piso || '',
    domicilio_puerta: t.puerta || '',
    domicilio_cp: t.cp || '',
    domicilio_poblacion: t.poblacion || '',
    domicilio_provincia: t.provincia || '',
    domicilio_pais: t.pais || 'ES',
    domicilio,
    porcentaje_propiedad: t.porcentaje !== '' && t.porcentaje != null ? Number(t.porcentaje) : 100,
    propietarios_extra: (state.propietariosExtra || []).map((p) => ({
      nombre: p.nombre || '',
      apellidos: p.apellidos || '',
      domicilio_base: p.domicilio_base || '',
      numero: p.numero || '',
      bloque: p.bloque || '',
      escalera: p.escalera || '',
      piso: p.piso || '',
      puerta: p.puerta || '',
      cp: p.cp || '',
      poblacion: p.poblacion || '',
      provincia: p.provincia || '',
      pais: p.pais || 'ES',
      domicilio: componerDomicilio(p),
      dni: p.dni || '',
      telefono: p.telefono || '',
      email: p.email || '',
      porcentaje: p.porcentaje !== '' && p.porcentaje != null ? Number(p.porcentaje) : null,
    })),
    contactos_extra: (state.contactosExtra || []).map((p) => ({
      nombre: p.nombre || '',
      apellidos: p.apellidos || '',
      domicilio_base: p.domicilio_base || '',
      numero: p.numero || '',
      bloque: p.bloque || '',
      escalera: p.escalera || '',
      piso: p.piso || '',
      puerta: p.puerta || '',
      cp: p.cp || '',
      poblacion: p.poblacion || '',
      provincia: p.provincia || '',
      pais: p.pais || 'ES',
      domicilio: componerDomicilio(p),
      dni: p.dni || '',
      telefono: p.telefono || '',
      email: p.email || '',
    })),
    comunicacion_key: state.comunicacionKey || 'titular',
    propietarios_ficha: buildPropietariosFichaFromState(state),
    contactos_asociados_ficha: buildContactosAsociadosFichaFromState(state),
  };
}

/** Únicos campos obligatorios de una persona en este modal. */
const LABEL_CAMPO_PERSONA = {
  nombre: { label: 'Nombre *', placeholder: 'Nombre' },
  apellidos: { label: 'Apellidos *', placeholder: 'Apellidos' },
  dni: { label: 'DNI / NIE / CIF *', placeholder: 'Documento' },
  telefono: { label: 'Teléfono', placeholder: 'Ej: 612 345 678' },
  email: { label: 'Email', placeholder: 'email@ejemplo.com' },
};

/** Claves obligatorias que a esta persona le faltan según Config APP (O por tipo). */
function faltantesPersona(persona, obligPersonaKeys, esPrincipal = false) {
  return faltantesPersonaConPrincipal(persona, obligPersonaKeys, esPrincipal);
}

function copiarDireccionInmueble(persona, dir) {
  if (!dir || typeof dir !== 'object') return persona;
  return {
    ...persona,
    tipo_via_id: dir.tipo_via_id || persona.tipo_via_id,
    tipo_via: dir.tipo_via || persona.tipo_via,
    domicilio_base: dir.domicilio_base || dir.calle || persona.domicilio_base,
    numero: dir.numero ?? persona.numero,
    bloque: dir.bloque ?? persona.bloque,
    escalera: dir.escalera ?? persona.escalera,
    piso: dir.piso ?? persona.piso,
    puerta: dir.puerta ?? persona.puerta,
    cp: dir.cp || dir.codigo_postal || persona.cp,
    poblacion: dir.poblacion || persona.poblacion,
    provincia: dir.provincia || persona.provincia,
    pais: dir.pais || persona.pais || 'ES',
  };
}

function PersonaDomicilioCampos({
  persona,
  onChange,
  tiposVia = [],
  direccionInmueble,
  comKey,
  obligPersonaKeys,
  labelsByPersonaKey = {},
}) {
  const set = (k, v) => onChange(sanearPartesDomicilioPersona({ ...persona, [k]: v }));
  const show = (pk) => debeMostrarCampoPersona(pk, obligPersonaKeys, persona);
  const lbl = (pk, fallback) => {
    const base = labelsByPersonaKey[pk] || fallback;
    const star = obligPersonaKeys?.has?.(pk) ? ' *' : '';
    return `${base}${star}`;
  };
  const hayDomicilio = PERSONA_DOMICILIO_KEYS.some((k) => show(k));
  if (!hayDomicilio) return null;
  const provinciaSel = (() => {
    const raw = String(persona.provincia || '').trim();
    if (!raw) return '';
    const byId = PROVINCIAS_ES.find((p) => p.id === raw.padStart(2, '0'));
    if (byId) return byId.id;
    const byName = PROVINCIAS_ES.find((p) => p.nombre.toLowerCase() === raw.toLowerCase());
    return byName?.id || '';
  })();

  const aplicarDesdePlaces = (parts) => {
    if (!parts) return;
    const calleRaw = String(parts.calle || '').trim();
    const numeroRaw = String(parts.numero || '').trim();
    const parsedVia = parseTipoViaCalle(
      [calleRaw, numeroRaw].filter(Boolean).join(numeroRaw && !calleRaw.includes(numeroRaw) ? `, ${numeroRaw}` : ''),
      tiposVia,
    );
    const next = { ...persona };
    if (parsedVia.nombreVia || calleRaw) next.domicilio_base = parsedVia.nombreVia || calleRaw;
    if (parsedVia.numero || numeroRaw) next.numero = parsedVia.numero || numeroRaw;
    let tipoViaId = parsedVia.tipoViaId || '';
    if (!tipoViaId && parsedVia.tipoViaCanon) {
      tipoViaId = normalizeAuxSelectValue(parsedVia.tipoViaCanon, tiposVia);
    }
    if (tipoViaId) {
      next.tipo_via_id = tipoViaId;
      const tv = tiposVia.find((t) => String(t.id) === String(tipoViaId));
      next.tipo_via = tv?.nombre || parsedVia.tipoViaCanon || next.tipo_via;
    } else if (parsedVia.tipoViaCanon) {
      next.tipo_via = parsedVia.tipoViaCanon;
    }
    const cp = String(parts.codigo_postal || '').trim();
    if (cp) next.cp = cp;
    const pob = resolverPoblacionPlaces(parts);
    if (pob) next.poblacion = pob;
    const provId = resolverProvinciaId(parts.provincia, PROVINCIAS_ES);
    if (provId) {
      next.provincia = PROVINCIAS_ES.find((p) => p.id === provId)?.nombre || String(parts.provincia || '').trim();
    } else if (parts.provincia) {
      next.provincia = String(parts.provincia).trim();
    }
    if (!next.pais) next.pais = 'ES';
    onChange(next);
  };

  const tipoViaSelected = normalizeAuxSelectValue(persona.tipo_via_id || persona.tipo_via, tiposVia);

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${BRD_MOVIL}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <span className="agente-f-label" style={{ ...labelStyle, margin: 0 }}>Dirección propietario</span>
        {direccionInmueble && (
          <button
            type="button"
            onClick={() => onChange(copiarDireccionInmueble(persona, direccionInmueble))}
            style={{
              border: `2px solid ${ACC_MOVIL}`,
              background: '#fff',
              color: ACC_MOVIL,
              fontWeight: 700,
              fontSize: 11,
              padding: '4px 8px',
              borderRadius: 8,
              cursor: 'pointer',
              fontFamily: 'inherit',
              whiteSpace: 'nowrap',
            }}
          >
            Igual propiedad
          </button>
        )}
      </div>

      {show('tipo_via_id') && (
        <>
          <span className="agente-f-label" style={labelStyle}>{lbl('tipo_via_id', 'Tipo de vía')}</span>
          <InpCampoMovil
            as="select"
            value={tipoViaSelected}
            onChange={(e) => {
              const id = e.target.value;
              const tv = tiposVia.find((t) => String(t.id) === String(id));
              onChange({
                ...persona,
                tipo_via_id: id,
                tipo_via: tv?.nombre || '',
              });
            }}
          >
            <option value="">Seleccionar tipo de vía</option>
            {tiposVia.map((tv) => (
              <option key={tv.id} value={tv.id}>{tv.nombre}</option>
            ))}
          </InpCampoMovil>
        </>
      )}

      {show('domicilio_base') && (
        <>
          <span className="agente-f-label" style={labelStyle}>{lbl('domicilio_base', 'Dirección')}</span>
          <AddressAutocompleteInput
            value={persona.domicilio_base || ''}
            onValueChange={(v) => set('domicilio_base', v)}
            onPlaceAddressResolved={aplicarDesdePlaces}
            className="agente-f-inp"
            style={{
              width: '100%',
              border: `2px solid ${BRD_MOVIL}`,
              borderRadius: 10,
              padding: '12px 14px',
              fontSize: 15,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
            }}
            placeholder="Calle (Google o manual)"
            name={`agente-prop-${comKey || 'x'}-domicilio`}
          />
        </>
      )}

      {(show('numero') || show('bloque') || show('escalera') || show('piso') || show('puerta')) && (
      <div style={rowPartesStyle}>
        {show('numero') && (
        <div style={cellParteStyle}>
          <span className="agente-f-label" style={labelStyle}>{lbl('numero', 'Número')}</span>
          <InpCampoMovil value={persona.numero || ''} onChange={(e) => set('numero', e.target.value)} placeholder="Nº" />
        </div>
        )}
        {show('bloque') && (
        <div style={cellParteStyle}>
          <span className="agente-f-label" style={labelStyle}>{lbl('bloque', 'Bloque')}</span>
          <InpCampoMovil value={persona.bloque || ''} onChange={(e) => set('bloque', e.target.value)} placeholder="Bloque" />
        </div>
        )}
        {show('escalera') && (
        <div style={cellParteStyle}>
          <span className="agente-f-label" style={labelStyle}>{lbl('escalera', 'Esc.')}</span>
          <InpCampoMovil value={persona.escalera || ''} onChange={(e) => set('escalera', e.target.value)} placeholder="Esc." />
        </div>
        )}
        {show('piso') && (
        <div style={cellParteStyle}>
          <span className="agente-f-label" style={labelStyle}>{lbl('piso', 'Altura')}</span>
          <InpCampoMovil value={persona.piso || ''} onChange={(e) => set('piso', e.target.value)} placeholder="Piso" />
        </div>
        )}
        {show('puerta') && (
        <div style={cellParteStyle}>
          <span className="agente-f-label" style={labelStyle}>{lbl('puerta', 'Pta.')}</span>
          <InpCampoMovil value={persona.puerta || ''} onChange={(e) => set('puerta', e.target.value)} placeholder="Puerta" />
        </div>
        )}
      </div>
      )}

      {(show('cp') || show('poblacion')) && (
      <div style={{ ...rowPartesStyle, marginTop: 8 }}>
        {show('cp') && (
        <div style={{ flex: '1 1 72px', minWidth: 72 }}>
          <span className="agente-f-label" style={labelStyle}>{lbl('cp', 'CP')}</span>
          <InpCampoMovil
            value={persona.cp || ''}
            onChange={(e) => set('cp', e.target.value.replace(/\D/g, '').slice(0, 5))}
            placeholder="03700"
            inputMode="numeric"
          />
        </div>
        )}
        {show('poblacion') && (
        <div style={{ flex: '2 1 120px', minWidth: 120 }}>
          <span className="agente-f-label" style={labelStyle}>{lbl('poblacion', 'Población')}</span>
          <InpCampoMovil
            value={persona.poblacion || ''}
            onChange={(e) => set('poblacion', e.target.value)}
            placeholder="Población"
          />
        </div>
        )}
      </div>
      )}

      {(show('provincia') || show('pais')) && (
      <div style={{ ...rowPartesStyle, marginTop: 4 }}>
        {show('provincia') && (
        <div style={{ flex: '2 1 140px', minWidth: 140 }}>
          <span className="agente-f-label" style={labelStyle}>{lbl('provincia', 'Provincia')}</span>
          <InpCampoMovil
            as="select"
            value={provinciaSel}
            onChange={(e) => {
              const prov = PROVINCIAS_ES.find((p) => p.id === e.target.value);
              set('provincia', prov?.nombre || '');
            }}
          >
            <option value="">— Provincia —</option>
            {PROVINCIAS_ES.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </InpCampoMovil>
        </div>
        )}
        {show('pais') && (
        <div style={{ flex: '1 1 100px', minWidth: 100 }}>
          <span className="agente-f-label" style={labelStyle}>{lbl('pais', 'País')}</span>
          <InpCampoMovil
            as="select"
            value={persona.pais || 'ES'}
            onChange={(e) => set('pais', e.target.value)}
          >
            {PAISES_DOMICILIO.map((p) => (
              <option key={p.code} value={p.code}>{p.nombre}</option>
            ))}
          </InpCampoMovil>
        </div>
        )}
      </div>
      )}
    </div>
  );
}

/**
 * Campos obligatorios del propietario/contacto.
 * Ampliar: se muestran SIEMPRE los 3 (nombre/apellidos/DNI) mientras la tarjeta
 * esté abierta. Antes solo se pintaban los vacíos; al escribir el último (o al
 * autofill de Chrome al enfocar) el componente hacía return null y el campo
 * desaparecía bajo el foco.
 */
function PersonaCamposFaltantes({
  persona,
  onChange,
  comKey,
  comunicacionKey,
  onComunicacion,
  titulo,
  onRemove,
  tiposVia,
  direccionInmueble,
  obligPersonaKeys,
  labelsByPersonaKey,
  contactoContext,
}) {
  const esPrincipal = !!(comKey && comunicacionKey === comKey);
  const set = (k, v) => onChange({ ...persona, [k]: v });
  const soft = useContactoSoftWarnings(comKey || 'persona');
  const ctxContacto = {
    idioma: contactoContext?.idioma,
    nacionalidad: contactoContext?.nacionalidad,
    pais: persona.pais || contactoContext?.pais || 'ES',
    dni: persona.dni,
    dni_nie: persona.dni,
  };
  const revisarCampo = (tipo, valor, aplicar) => {
    soft.checkField(tipo, valor, ctxContacto).then((v) => {
      if (v !== valor) aplicar(v);
    });
  };
  const faltantes = faltantesPersona(persona, obligPersonaKeys, esPrincipal);
  const [abierta, setAbierta] = useState(() => faltantes.length > 0);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (faltantes.length > 0) setAbierta(true);
  }, [faltantes.length]);

  useEffect(() => {
    if (esPrincipal && faltantes.includes('telefono')) setAbierta(true);
  }, [esPrincipal, comunicacionKey, faltantes.includes('telefono')]);

  if (!abierta) return null;

  const cerrarSiCompletoYFuera = (e) => {
    const next = e?.relatedTarget;
    if (wrapRef.current && next && wrapRef.current.contains(next)) return;
    if (faltantesPersona(persona, obligPersonaKeys, esPrincipal).length === 0) setAbierta(false);
  };

  return (
    <div
      ref={wrapRef}
      onBlur={cerrarSiCompletoYFuera}
      style={{
        marginBottom: 12,
        padding: '12px 12px 10px',
        borderRadius: 12,
        border: `2px solid ${comKey && comunicacionKey === comKey ? ACC_MOVIL : BRD_MOVIL}`,
        background: '#fff',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
        {comKey ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>
            <input
              type="radio"
              name="agente-com-prop"
              checked={comunicacionKey === comKey}
              onChange={() => {
                onComunicacion?.(comKey);
              }}
            />
            {titulo}
          </label>
        ) : (
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{titulo}</div>
        )}
        {onRemove && (
          <button type="button" onClick={onRemove} style={{
            border: 'none', background: 'transparent', color: '#b91c1c', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}>
            Quitar
          </button>
        )}
      </div>

      {PERSONA_BASICA_KEYS.filter((k) => {
        if (k === 'telefono' && esPrincipal) return true;
        return debeMostrarCampoPersona(k, obligPersonaKeys, persona);
      }).map((k) => (
        <div key={k}>
          <span className="agente-f-label" style={labelStyle}>
            {labelsByPersonaKey?.[k] || LABEL_CAMPO_PERSONA[k]?.label || k}
            {(obligPersonaKeys?.has?.(k) || (k === 'telefono' && esPrincipal)) ? ' *' : ''}
          </span>
          <InpCampoMovil
            value={persona[k] || ''}
            onChange={(e) => {
              const v = e.target.value;
              set(k, v);
              if (k === 'dni' && debeRevisarDocumentoEnInput(v)) {
                revisarCampo('dni', v, (nv) => set('dni', nv));
              }
            }}
            onBlur={() => {
              const v = persona[k] || '';
              if (k === 'apellidos') revisarCampo('apellidos', v, (nv) => set('apellidos', nv));
              else if (k === 'dni') revisarCampo('dni', v, (nv) => set('dni', nv));
              else if (k === 'telefono') revisarCampo('telefono', v, (nv) => set('telefono', nv));
              else if (k === 'email') revisarCampo('email', v, (nv) => set('email', nv));
            }}
            placeholder={LABEL_CAMPO_PERSONA[k]?.placeholder || k}
            autoComplete="off"
            name={`agente-prop-${comKey || 'x'}-${k}`}
            type={k === 'email' ? 'email' : k === 'telefono' ? 'tel' : 'text'}
          />
        </div>
      ))}

      {soft.modal}

      <PersonaDomicilioCampos
        persona={persona}
        onChange={onChange}
        tiposVia={tiposVia}
        direccionInmueble={direccionInmueble}
        comKey={comKey}
        obligPersonaKeys={obligPersonaKeys}
        labelsByPersonaKey={labelsByPersonaKey}
      />
    </div>
  );
}

/**
 * Bloque propietarios/contactos al estilo del wizard móvil (cap4).
 * Una vez abierto (hay pendientes), se mantiene visible en esta sesión del
 * modal aunque el autofill complete los 3 campos al primer foco.
 */
export default function BloquePropietariosAgente({
  state,
  onChange,
  tiposVia = [],
  direccionInmueble,
  camposObligatorios = [],
  contactoContext,
}) {
  const { personaKeys: obligPersonaKeys, labelsByPersonaKey } = useMemo(
    () => buildObligatoriosPersonaDesdeConfig(camposObligatorios),
    [camposObligatorios],
  );

  if (!state) return null;
  const { titular, propietariosExtra = [], contactosExtra = [], comunicacionKey } = state;

  const setState = (patch) => onChange({ ...state, ...patch });

  const hayAlgoPendiente = faltantesPersona(titular, obligPersonaKeys, comunicacionKey === 'titular').length > 0
    || propietariosExtra.some((p, i) => faltantesPersona(p, obligPersonaKeys, comunicacionKey === `prop:${i}`).length > 0)
    || contactosExtra.some((p, i) => faltantesPersona(p, obligPersonaKeys, comunicacionKey === `cont:${i}`).length > 0);

  const [mantenerVisible, setMantenerVisible] = useState(false);
  useEffect(() => {
    if (hayAlgoPendiente) setMantenerVisible(true);
  }, [hayAlgoPendiente]);

  if (!hayAlgoPendiente && !mantenerVisible) return null;

  return (
    <div className="agente-movil-campos" style={{
      marginBottom: 14,
      background: '#fff7ed',
      border: '2px solid #f97316',
      borderRadius: 12,
      padding: '12px 14px',
    }}>
      <div style={{
        fontSize: 12, fontWeight: 800, color: '#c2410c',
        textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8,
      }}>
        Propietarios y contactos
      </div>

      <PersonaCamposFaltantes
        titulo="Propietario 1"
        persona={titular}
        comKey="titular"
        comunicacionKey={comunicacionKey}
        onComunicacion={(k) => setState({ comunicacionKey: k })}
        onChange={(p) => setState({ titular: p })}
        tiposVia={tiposVia}
        direccionInmueble={direccionInmueble}
        obligPersonaKeys={obligPersonaKeys}
        labelsByPersonaKey={labelsByPersonaKey}
        contactoContext={contactoContext}
      />

      {propietariosExtra.map((p, i) => (
        <PersonaCamposFaltantes
          key={`prop-${i}`}
          titulo={`Propietario ${i + 2}`}
          persona={p}
          comKey={`prop:${i}`}
          comunicacionKey={comunicacionKey}
          onComunicacion={(k) => setState({ comunicacionKey: k })}
          onChange={(next) => {
            const arr = propietariosExtra.slice();
            arr[i] = next;
            setState({ propietariosExtra: arr });
          }}
          onRemove={() => {
            const arr = propietariosExtra.filter((_, j) => j !== i);
            setState({
              propietariosExtra: arr,
              comunicacionKey: comunicacionKey === `prop:${i}` ? 'titular' : comunicacionKey,
            });
          }}
          tiposVia={tiposVia}
          direccionInmueble={direccionInmueble}
          obligPersonaKeys={obligPersonaKeys}
          labelsByPersonaKey={labelsByPersonaKey}
          contactoContext={contactoContext}
        />
      ))}

      {contactosExtra.map((p, i) => (
        <PersonaCamposFaltantes
          key={`cont-${i}`}
          titulo={`Contacto ${i + 1}`}
          persona={p}
          comKey={`cont:${i}`}
          comunicacionKey={comunicacionKey}
          onComunicacion={(k) => setState({ comunicacionKey: k })}
          onChange={(next) => {
            const arr = contactosExtra.slice();
            arr[i] = next;
            setState({ contactosExtra: arr });
          }}
          onRemove={() => {
            const arr = contactosExtra.filter((_, j) => j !== i);
            setState({
              contactosExtra: arr,
              comunicacionKey: comunicacionKey === `cont:${i}` ? 'titular' : comunicacionKey,
            });
          }}
          tiposVia={tiposVia}
          direccionInmueble={direccionInmueble}
          obligPersonaKeys={obligPersonaKeys}
          labelsByPersonaKey={labelsByPersonaKey}
          contactoContext={contactoContext}
        />
      ))}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
        <button
          type="button"
          className="agente-add-btn agente-add-btn--prop"
          onClick={() => setState({ propietariosExtra: [...propietariosExtra, emptyPersona()] })}
          style={estiloAddBtnMovil('prop')}
        >
          + Añadir otro propietario
        </button>
        <button
          type="button"
          className="agente-add-btn agente-add-btn--cont"
          onClick={() => setState({ contactosExtra: [...contactosExtra, emptyPersona()] })}
          style={estiloAddBtnMovil('cont')}
        >
          + Añadir contacto
        </button>
      </div>
    </div>
  );
}
